import * as THREE from './peepz-promo-three.module.js';

// pdf.js is only needed as a FALLBACK now -- box faces normally load from
// pre-baked WebP textures (see loadFaceMaterial below and
// tools/bake-peepz-faces), so it's no longer a blocking <script> on every
// page view (~330KB, plus a ~1MB worker fetched on first use). It's pulled
// in on demand, the first time a face actually has no baked texture yet
// (e.g. a brand new character whose PDFs were dropped in but not baked).
const PDFJS_VERSION = '3.11.174';
const PDFJS_BASE = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/`;
let pdfJsPromise = null;
function ensurePdfJs() {
  if (window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc ||= `${PDFJS_BASE}pdf.worker.min.js`;
    return Promise.resolve(window.pdfjsLib);
  }
  if (!pdfJsPromise) {
    pdfJsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${PDFJS_BASE}pdf.min.js`;
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}pdf.worker.min.js`;
        resolve(window.pdfjsLib);
      };
      script.onerror = () => {
        pdfJsPromise = null;
        reject(new Error('pdf.js failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return pdfJsPromise;
}

/**
 * Rounded-box geometry that keeps BoxGeometry's 6 per-face material groups
 * intact so each face could later take its own texture. Ported from
 * tools/box-3d-preview/index.html -- same closed-form "push every vertex
 * toward a rounded-box surface" approach, just without the texture/PDF/
 * shimmer machinery this hero doesn't need yet.
 */
function createRoundedBoxGeometry(w, h, d, radius, segments) {
  const geometry = new THREE.BoxGeometry(w, h, d, segments, segments, segments);
  const hw = w / 2, hh = h / 2, hd = d / 2;
  const r = Math.max(0, Math.min(radius, Math.min(hw, hh, hd) * 0.98));
  if (r <= 0.0001) return geometry;

  const pos = geometry.attributes.position;
  const norm = geometry.attributes.normal;
  const half = [hw, hh, hd];
  const core = [hw - r, hh - r, hd - r];
  const clamped = [0, 0, 0];
  const offset = [0, 0, 0];

  for (let i = 0; i < pos.count; i++) {
    const coords = [pos.getX(i), pos.getY(i), pos.getZ(i)];
    for (let a = 0; a < 3; a++) {
      clamped[a] = Math.max(-core[a], Math.min(core[a], coords[a]));
      offset[a] = coords[a] - clamped[a];
    }
    const len = Math.sqrt(offset[0] ** 2 + offset[1] ** 2 + offset[2] ** 2);
    if (len > 1e-8) {
      const inv = 1 / len;
      const nx = offset[0] * inv, ny = offset[1] * inv, nz = offset[2] * inv;
      pos.setXYZ(i, clamped[0] + nx * r, clamped[1] + ny * r, clamped[2] + nz * r);
      norm.setXYZ(i, nx, ny, nz);
    }
  }
  pos.needsUpdate = true;
  norm.needsUpdate = true;
  return geometry;
}

function mix(hexA, hexB, t) {
  const a = new THREE.Color(hexA);
  const b = new THREE.Color(hexB);
  return a.clone().lerp(b, t);
}

// A soft radial-alpha blob used as a drop shadow under each box -- cheap
// stand-in for real contact shadows, and matches the vignette-y "product
// shot" look instead of the box looking like it's floating with no anchor.
function createShadowTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 128;
  const ctx = canvas.getContext('2d');
  const gradient = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  gradient.addColorStop(0, 'rgba(0,0,0,0.4)'); // ~15% darker than the original 0.35
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(canvas);
}

/**
 * Ported from tools/box-3d-preview/index.html's buildSoftboxEnvironment --
 * a ring of alternating bright/dim panels plus an overhead key panel, baked
 * via PMREM into an environment map. No analytical lights: a directional
 * light would put one hard, sweeping specular hotspot on the shimmer PDF
 * faces' metal/iridescent material (the exact blowout that tool's own
 * comments describe chasing down), where this gives a broad, soft highlight
 * that can't blow out no matter how the box turns.
 */
function buildSoftboxEnvironment() {
  const envScene = new THREE.Scene();
  envScene.background = new THREE.Color(0x101014);
  const hotMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(0xffffff).multiplyScalar(2.8), side: THREE.DoubleSide, toneMapped: false });
  const fillMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(0xffffff).multiplyScalar(0.7), side: THREE.DoubleSide, toneMapped: false });
  const topMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(0xffffff).multiplyScalar(3.6), side: THREE.DoubleSide, toneMapped: false });
  const ringGeo = new THREE.PlaneGeometry(12, 14);
  const radius = 9.9;
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2;
    const panel = new THREE.Mesh(ringGeo, i % 2 === 1 ? hotMat : fillMat);
    panel.position.set(radius * Math.sin(angle), 5, radius * Math.cos(angle));
    panel.lookAt(0, 0, 0);
    envScene.add(panel);
  }
  const topPanel = new THREE.Mesh(new THREE.PlaneGeometry(16, 16), topMat);
  topPanel.position.set(0, 14, 0);
  topPanel.rotation.x = Math.PI / 2;
  envScene.add(topPanel);
  return envScene;
}

/**
 * Renders page 1 of a PDF (fetched by URL) to a canvas at targetWidth,
 * returning both that diffuse canvas and the loaded pdf.js document (needed
 * again by buildMetalMaskFromPdf below). Ported from the tool's
 * loadFacePdf/loadFacePdf, just fetch-based instead of File-based since
 * there's no file input here.
 */
async function fetchPdfPageCanvas(url, targetWidth) {
  await ensurePdfJs();
  const res = await fetch(url);
  // A missing file (character art not dropped in yet) 404s -- without this
  // check, that error page's HTML would get handed to pdfjsLib as if it
  // were PDF bytes, failing with a much less obvious parse error instead.
  if (!res.ok) throw new Error(`${url} responded ${res.status}`);
  const buf = await res.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: buf }).promise;
  const page = await pdf.getPage(1);
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = targetWidth / baseViewport.width;
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport }).promise;
  return { canvas, pdfInfo: { doc: pdf, pageNumber: 1 } };
}

/**
 * Shimmer mask, PDF variant, ported verbatim from the tool: if the PDF was
 * exported with Illustrator's "Create Acrobat Layers from Top-Level Layers"
 * option, the target layer becomes a named Optional Content Group (OCG) we
 * can toggle. Rendering the page TWICE -- once normally, once with only
 * that OCG turned off -- and diffing pixel-for-pixel isolates exactly that
 * layer's own content, without needing to hide every other OCG (which
 * would wrongly sweep in any unlayered content pdf.js can't hide at all).
 */
async function buildMetalMaskFromPdf(pdfInfo, layerName, w, h) {
  const page = await pdfInfo.doc.getPage(pdfInfo.pageNumber);
  const baseViewport = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: w / baseViewport.width });

  const occCheck = await pdfInfo.doc.getOptionalContentConfig();
  const groups = occCheck.getGroups() || {};
  const targetId = Object.entries(groups).find(([, info]) => info.name === layerName)?.[0];
  if (!targetId) return null;

  const canvasWith = document.createElement('canvas');
  canvasWith.width = w;
  canvasWith.height = h;
  await page.render({ canvasContext: canvasWith.getContext('2d'), viewport }).promise;

  const occHidden = await pdfInfo.doc.getOptionalContentConfig();
  occHidden.setVisibility(targetId, false);
  const canvasWithout = document.createElement('canvas');
  canvasWithout.width = w;
  canvasWithout.height = h;
  await page.render({
    canvasContext: canvasWithout.getContext('2d'),
    viewport,
    optionalContentConfigPromise: Promise.resolve(occHidden),
  }).promise;

  const d1 = canvasWith.getContext('2d').getImageData(0, 0, w, h).data;
  const d2 = canvasWithout.getContext('2d').getImageData(0, 0, w, h).data;
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = w;
  maskCanvas.height = h;
  const mctx = maskCanvas.getContext('2d');
  const maskImg = mctx.createImageData(w, h);
  const md = maskImg.data;
  for (let i = 0; i < d1.length; i += 4) {
    const diff = Math.abs(d1[i] - d2[i]) + Math.abs(d1[i + 1] - d2[i + 1]) + Math.abs(d1[i + 2] - d2[i + 2]);
    const on = diff > 24;
    md[i] = md[i + 1] = md[i + 2] = on ? 255 : 0;
    md[i + 3] = 255;
  }
  mctx.putImageData(maskImg, 0, 0);
  return maskCanvas;
}

// material.dispose() alone doesn't dispose its texture maps -- needed since
// PDF-driven face materials carry several (diffuse/metalness/iridescence/
// clearcoat/thickness), and the flat placeholder materials they replace (or
// get replaced by) don't have any, so this covers both safely.
function disposeMaterial(material) {
  for (const key of ['map', 'metalnessMap', 'iridescenceMap', 'clearcoatMap', 'iridescenceThicknessMap']) {
    if (material[key]) material[key].dispose();
  }
  material.dispose();
}

function hexToRgb(hex) {
  const v = parseInt(hex.slice(1), 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

// Foil/metallic areas in print art are usually filled with a black or
// spot-color placeholder (that finish isn't real ink), so the artwork's own
// pixel color there is meaningless -- repaint the masked region with a
// neutral tint before it becomes the metal's base color, otherwise "metal"
// ends up rendering as flat black.
function paintMaskedRegion(diffuseCanvas, maskCanvas, rgb) {
  const w = diffuseCanvas.width, h = diffuseCanvas.height;
  const dctx = diffuseCanvas.getContext('2d');
  const dImg = dctx.getImageData(0, 0, w, h);
  const md = maskCanvas.getContext('2d').getImageData(0, 0, maskCanvas.width, maskCanvas.height).data;
  const dd = dImg.data;
  for (let i = 0; i < dd.length; i += 4) {
    if (md[i] > 128) {
      dd[i] = rgb[0]; dd[i + 1] = rgb[1]; dd[i + 2] = rgb[2];
    }
  }
  dctx.putImageData(dImg, 0, 0);
}

// Bounding box of the masked (shimmer) pixels, so the iridescence gradient
// below stretches across just the shimmer artwork instead of wasting most
// of its range on off-shimmer pixels.
function maskBounds(maskCanvas) {
  const w = maskCanvas.width, h = maskCanvas.height;
  const data = maskCanvas.getContext('2d').getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = 0, y1 = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (data[(y * w + x) * 4] > 128) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < x0) return { x0: 0, y0: 0, x1: w, y1: h };
  return { x0, y0, x1, y1 };
}

// clearcoatMap: full clearcoat on the shimmer mask, only a light partial
// coat elsewhere (a printed box still has some lamination sheen).
function buildClearcoatMap(maskCanvas, outsideFraction) {
  const w = maskCanvas.width, h = maskCanvas.height;
  const src = maskCanvas.getContext('2d').getImageData(0, 0, w, h).data;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const octx = out.getContext('2d');
  const dst = octx.createImageData(w, h);
  const outsideValue = Math.round(255 * outsideFraction);
  for (let i = 0; i < src.length; i += 4) {
    const v = src[i] > 128 ? 255 : outsideValue;
    dst.data[i] = v; dst.data[i + 1] = v; dst.data[i + 2] = v; dst.data[i + 3] = 255;
  }
  octx.putImageData(dst, 0, 0);
  return out;
}

// Grows the mask's white regions outward by `radius` px (an approximate
// dilate, via additively-blended offset copies of a pure black/white
// image). The shimmer layer on these particular faces is a thin outline --
// only a few px wide even at the full source resolution -- so at the box's
// actual on-screen size that line is thinner than a single rendered pixel;
// no texture filtering mode makes a sub-pixel-wide feature show up
// reliably. Thickening it here, once, at texture-build time is what
// actually fixes that, rather than fighting it via filtering settings.
function dilateMask(sourceCanvas, radius) {
  const w = sourceCanvas.width, h = sourceCanvas.height;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d');
  ctx.drawImage(sourceCanvas, 0, 0);
  ctx.globalCompositeOperation = 'lighter';
  for (let dx = -radius; dx <= radius; dx += radius) {
    for (let dy = -radius; dy <= radius; dy += radius) {
      if (dx === 0 && dy === 0) continue;
      ctx.drawImage(sourceCanvas, dx, dy);
    }
  }
  return out;
}

// Diagonal grayscale gradient used as iridescenceThicknessMap: film
// thickness varying across the surface is what shows several hues at once
// (a holographic "rainbow" look) instead of the whole area shifting through
// one color together as the box turns.
function buildIridescenceGradient(w, h, bounds) {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createLinearGradient(bounds.x0, bounds.y0, bounds.x1, bounds.y1);
  grad.addColorStop(0, '#000000');
  grad.addColorStop(1, '#ffffff');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
  return canvas;
}

// The box wrap is capped at 420px CSS width (see .peepz-character__box-wrap),
// and any one face fills less than that -- 900px covers even a 2x-DPR
// screen with real margin. Was 1400px, which is a bigger source canvas
// than any actual device needs, and (since a shimmer-layer face renders
// the PDF THREE times over -- once for the diffuse canvas here, twice more
// for the with/without-layer mask diff below) multiplies straight through
// into real memory and load-time cost per face. This alone was a solid
// chunk of what was pushing "Open Box" over iOS's tolerance.
const FACE_PDF_TARGET_WIDTH = 900;

/**
 * Loads a PDF face by URL and builds its Three.js material: a real
 * MeshPhysicalMaterial with metalness/clearcoat/iridescence confined (via
 * the mask above) to the `shimmer` OCG layer if the PDF has one, or a plain
 * MeshStandardMaterial diffuse-only fallback if it doesn't.
 */
async function buildPdfFaceMaterial(url, layerName, metalColorHex) {
  const { canvas, pdfInfo } = await fetchPdfPageCanvas(url, FACE_PDF_TARGET_WIDTH);
  const rawMaskCanvas = await buildMetalMaskFromPdf(pdfInfo, layerName, canvas.width, canvas.height);

  if (rawMaskCanvas) {
    // Confirmed by testing: a uniform metal material with no map reflected
    // clearly, but this masked one didn't, even after forcing full-res (no
    // mipmap) texture sampling -- the line is thinner than a single
    // rendered screen pixel at the box's actual on-screen size, and no
    // filtering mode makes a sub-pixel feature show up reliably. Dilating
    // the mask once at build time is what actually fixes that.
    const maskCanvas = dilateMask(rawMaskCanvas, 3);
    paintMaskedRegion(canvas, maskCanvas, hexToRgb(metalColorHex));
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    // Without this, a real (non-flat-color) diffuse texture viewed at any
    // oblique angle -- which the box's fixed 3/4 camera always is, for at
    // least one of its two textured faces -- comes out visibly blurry.
    // Matches the tool's own TEXTURE_ANISOTROPY cap.
    tex.anisotropy = 4;

    const maskTex = new THREE.CanvasTexture(maskCanvas);
    const iridescenceThicknessMap = new THREE.CanvasTexture(
      buildIridescenceGradient(canvas.width, canvas.height, maskBounds(maskCanvas))
    );
    const clearcoatMap = new THREE.CanvasTexture(buildClearcoatMap(maskCanvas, 0.35));
    return new THREE.MeshPhysicalMaterial({
      map: tex,
      metalnessMap: maskTex,
      metalness: 1,
      roughness: 0.28,
      envMapIntensity: 1.6,
      clearcoat: 0.25,
      clearcoatMap,
      clearcoatRoughness: 0.2,
      iridescence: 0.7,
      iridescenceMap: maskTex,
      iridescenceIOR: 1.9,
      iridescenceThicknessRange: [100, 1000],
      iridescenceThicknessMap,
    });
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return new THREE.MeshStandardMaterial({ map: tex, roughness: 0.85 });
}

// ---------------------------------------------------------------------------
// Baked face textures.
//
// tools/bake-peepz-faces renders each face PDF once, offline, into two small
// WebP files (see that script's header for the exact channel layout):
//   <name>.webp     the diffuse art (shimmer area already repainted metal-grey)
//   <name>-fx.webp  R = shimmer mask, G = iridescence thickness ramp, B = mask
//                   (1x1 black when the face has no shimmer layer)
// Loading those replaces, per shimmer face: a PDF download, a pdf.js parse,
// THREE full-size page renders, ~6 full-frame getImageData passes and four
// full-size textures (diffuse, mask, thickness ramp, clearcoat) with two image
// decodes and two textures (the packed fx texture serves as the metalness,
// iridescence AND thickness map at once). The clearcoat map is the one extra:
// it's derived from the fx mask at half resolution, since it only modulates
// a subtle sheen.
function decodeImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // The asset CDN is a different origin than the storefront, and a
    // cross-origin image can't be uploaded to WebGL unless it was requested
    // with CORS (the CDN does answer with the right headers).
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onerror = () => reject(new Error(`${url} failed to load`));
    img.onload = () => {
      // decode() gets the pixels decoded off the main thread now, so the
      // texture upload doesn't block on a synchronous decode. Best-effort
      // only: it waits on the rendering lifecycle, so in a hidden/background
      // tab it can sit unresolved indefinitely -- never worth holding the
      // face back for (the upload just decodes synchronously instead).
      Promise.race([img.decode().catch(() => {}), new Promise((r) => setTimeout(r, 250))]).then(() => resolve(img));
    };
    img.src = url;
  });
}

const CLEARCOAT_MAP_SCALE = 0.5;
const CLEARCOAT_OUTSIDE_MASK = 0.35; // same partial "lamination sheen" the per-pixel version used off the shimmer mask

function buildClearcoatMapFromFx(fxImage) {
  const w = Math.max(1, Math.round(fxImage.naturalWidth * CLEARCOAT_MAP_SCALE));
  const h = Math.max(1, Math.round(fxImage.naturalHeight * CLEARCOAT_MAP_SCALE));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(fxImage, 0, 0, w, h);
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;
  const floor = 255 * CLEARCOAT_OUTSIDE_MASK;
  for (let i = 0; i < d.length; i += 4) {
    const v = floor + (255 - floor) * (d[i] / 255);
    d[i] = d[i + 1] = d[i + 2] = v;
    d[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return new THREE.CanvasTexture(canvas);
}

async function buildBakedFaceMaterial(texUrl, fxUrl) {
  const [diffuseImage, fxImage] = await Promise.all([
    decodeImage(texUrl),
    // A missing fx file just means "diffuse only" -- never worth failing the face over.
    fxUrl ? decodeImage(fxUrl).catch(() => null) : Promise.resolve(null),
  ]);

  const map = new THREE.Texture(diffuseImage);
  map.colorSpace = THREE.SRGBColorSpace;
  // Without this, a real (non-flat-color) diffuse texture viewed at any
  // oblique angle -- which the box's fixed 3/4 camera always is, for at
  // least one of its two textured faces -- comes out visibly blurry.
  map.anisotropy = 4;
  map.needsUpdate = true;

  if (!fxImage || fxImage.naturalWidth <= 1) {
    return new THREE.MeshStandardMaterial({ map, roughness: 0.85 });
  }

  // Data, not color: left in the default (linear) color space.
  const fx = new THREE.Texture(fxImage);
  fx.needsUpdate = true;
  return new THREE.MeshPhysicalMaterial({
    map,
    metalnessMap: fx, // reads B
    metalness: 1,
    roughness: 0.28,
    envMapIntensity: 1.6,
    clearcoat: 0.25,
    clearcoatMap: buildClearcoatMapFromFx(fxImage),
    clearcoatRoughness: 0.2,
    iridescence: 0.7,
    iridescenceMap: fx, // reads R
    iridescenceIOR: 1.9,
    iridescenceThicknessRange: [100, 1000],
    iridescenceThicknessMap: fx, // reads G
  });
}

// Which face art a box canvas has: baked WebP textures when the template
// provides them (data-<side>-tex-url / -fx-url), else -- for a character whose
// PDFs were dropped in but not baked yet, or if the baked files 404 -- the
// original runtime PDF pipeline (which loads pdf.js on demand).
async function loadFaceMaterial(canvas, side) {
  const texUrl = canvas.dataset[`${side}TexUrl`];
  if (texUrl) {
    try {
      return await buildBakedFaceMaterial(texUrl, canvas.dataset[`${side}FxUrl`]);
    } catch (err) {
      console.warn('Peepz hero: baked face texture unavailable, falling back to the PDF', texUrl, err);
    }
  }
  const pdfUrl = canvas.dataset[`${side}PdfUrl`];
  if (!pdfUrl) return null;
  return buildPdfFaceMaterial(pdfUrl, 'shimmer', '#d7d7dc');
}

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function clamp01(v) {
  return Math.min(1, Math.max(0, v));
}

// -- Everything below through bagSeamPoints supports BagViewer/the "open
// box" unboxing flow on PeepzCharacterSectionComponent (the foil bag that
// flies out, gets torn open along its top seam, and falls away).

function smoothstep(edge0, edge1, x) {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

// Pushes the bag's front/back faces outward toward their centers, on top of
// whatever shape createRoundedBoxGeometry already made -- deliberately NOT
// welded first: THREE.BoxGeometry already keeps each face's vertices
// unshared with its neighbors, so a boundary vertex none of the lobes below
// move keeps its own face's flat, un-smoothed normal instead of blending
// with the adjacent side wall's, giving the bag a crisp, flat, heat-sealed
// rim instead of a soft rounded bevel.
//
// Teddy-bear silhouette (round head, two ear bumps, one big round body,
// two stubby arms hanging at its sides, two stubby legs) rather than the
// T-pose stick figure from earlier passes -- rounder and chunkier, closer
// to an actual plush toy. Lobe centers sit closer to the body and their
// radii overlap generously (e.g. armCy/legCy pulled in toward bodyCy) so
// the neck/shoulder/hip "joints" are wide, gradual blends rather than a
// thin pinch between two separate bumps. Each lobe's falloff band spans
// almost its entire radius (0.05 -> 1.0) so there's essentially no flat
// plateau left at a lobe's own center, just one continuous gradient.
// Summed, not max()'d, across lobes -- max() gives each lobe a hard
// boundary where its dominance hands off to the next lobe, and that
// handoff itself reads as an edge; summing instead lets overlapping lobes
// blend into one soft, amorphous rise. The body lobe is large and carries
// most of the weight -- it's the "overall bulk" -- while ears/arms/legs
// stay at LOW weight (0.15 -> 0.2) so they add barely any extra visible
// detail on their own; increasing those along with the body (tried first)
// just made individual limbs more legible, not the bag overall bulkier.
// Lobe centers sit close to the body and their radii overlap generously
// so the neck/shoulder/hip "joints" are wide, gradual blends rather than
// a thin pinch between two separate bumps. Each lobe's falloff band spans
// almost its entire radius (0.05 -> 1.0) so there's essentially no flat
// plateau left at a lobe's own center, just one continuous gradient.
// Still checked to stay fully zero before the box's true edges, so the
// sealed rim stays flat. All the A/Cx (horizontal) figures are 0.75x and
// the B/Cy (vertical) figures are stretched up from an earlier, more
// evenly-proportioned pass -- a taller, narrower toy silhouette --
// margins re-checked against the box's true edges after the stretch.
function bulgePouchFaces(geometry, w, h, amount) {
  const pos = geometry.attributes.position;
  const norm = geometry.attributes.normal;
  const hw = w / 2, hh = h / 2;

  const headCy = hh * 0.48;
  const headA = hw * 0.375, headB = hh * 0.29;

  const earCx = hw * 0.225, earCy = hh * 0.72;
  const earA = hw * 0.135, earB = hh * 0.13;

  const bodyCy = -hh * 0.12;
  const bodyA = hw * 0.585, bodyB = hh * 0.68;

  const armCx = hw * 0.345, armCy = -hh * 0.32;
  const armA = hw * 0.1575, armB = hh * 0.37;

  const legCx = hw * 0.165, legCy = -hh * 0.56;
  const legA = hw * 0.225, legB = hh * 0.3;

  function lobeFalloff(x, y, cx, cy, a, b) {
    const dx = (x - cx) / a, dy = (y - cy) / b;
    const dist = Math.sqrt(dx * dx + dy * dy);
    return 1 - smoothstep(0.05, 1.0, dist);
  }

  for (let i = 0; i < pos.count; i++) {
    const nx = norm.getX(i), ny = norm.getY(i), nz = norm.getZ(i);
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);

    const headFalloff = lobeFalloff(x, y, 0, headCy, headA, headB);
    const earLFalloff = lobeFalloff(x, y, -earCx, earCy, earA, earB);
    const earRFalloff = lobeFalloff(x, y, earCx, earCy, earA, earB);
    const bodyFalloff = lobeFalloff(x, y, 0, bodyCy, bodyA, bodyB);
    const armLFalloff = lobeFalloff(x, y, -armCx, armCy, armA, armB);
    const armRFalloff = lobeFalloff(x, y, armCx, armCy, armA, armB);
    const legLFalloff = lobeFalloff(x, y, -legCx, legCy, legA, legB);
    const legRFalloff = lobeFalloff(x, y, legCx, legCy, legA, legB);

    const raw =
      headFalloff * 0.65 +
      (earLFalloff + earRFalloff) * 0.15 +
      bodyFalloff * 0.9 +
      (armLFalloff + armRFalloff) * 0.18 +
      (legLFalloff + legRFalloff) * 0.2;
    const falloff = Math.min(1, raw);
    const push = amount * falloff;
    pos.setXYZ(i, x + nx * push, y + ny * push, z + nz * push);
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
}

// Loaded via a plain <img> + 2d canvas rather than THREE.TextureLoader
// directly -- the source SVG has transparent areas outside its drawn
// shapes, and an untouched-alpha texture under a metallic material reads as
// "no texture at all" (transparent pixels contribute black). Compositing
// onto an opaque backing first guarantees the pattern is visible everywhere.
function loadPatternTexture(url, size) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#f0eee8';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      texture.anisotropy = 4;
      resolve(texture);
    };
    img.onerror = reject;
    img.src = url;
  });
}

// A deterministic (seeded) pseudo-random 0..1 value -- so the torn seam's
// zigzag has fixed, repeatable jaggedness instead of a different-looking
// tear on every render.
function seededRandom(seed) {
  const x = Math.sin(seed * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function buildTearJitter(steps, jitterAmount, seed) {
  const jitter = [];
  for (let i = 0; i <= steps; i++) {
    if (i === 0 || i === steps) {
      jitter.push(0);
      continue;
    }
    jitter.push((seededRandom(seed + i * 7.13) - 0.5) * 2 * jitterAmount);
  }
  return jitter;
}

// The bag's top seam "unzipping" left-to-right: for each column x, the
// visible bag boundary sits at y=0 (sealed) once x is past the tear front,
// or dips down to a jittered inset (torn open, character showing through)
// once x is behind the tear front.
function bagSeamPoints(fraction, jitter, steps, inset) {
  const pts = [];
  for (let i = 0; i <= steps; i++) {
    const x = i / steps;
    const y = fraction > 0 && x <= fraction ? clamp01(inset + jitter[i]) : 0;
    pts.push({ x, y });
  }
  return pts;
}

function pointsToCss(pts) {
  return pts.map((p) => `${(p.x * 100).toFixed(2)}% ${(p.y * 100).toFixed(2)}%`).join(', ');
}

// ---------------------------------------------------------------------------
// One WebGL context shared by every BoxViewer on the page.
//
// Each box used to create its own WebGLRenderer: on desktop that's three live
// contexts at once (plus one per character section further down the page),
// each baking its own identical PMREM environment map and compiling its own
// copy of the same MeshPhysical iridescence/clearcoat shader -- the heaviest
// program in the scene, compiled synchronously on the main thread once per
// context. Here the renderer, the environment and the compiled programs
// exist once; a BoxViewer just draws its scene into a sub-viewport of the
// shared drawing buffer and copies that region onto its own 2D canvas.
//
// The context is reference-counted and kept alive for GL_IDLE_TEARDOWN_MS
// after the last viewer goes away: mobile disposes each box the instant it
// scrolls out of view, and rebuilding a context + environment + programs on
// every scroll back in would throw away most of what sharing saves.
const GL_IDLE_TEARDOWN_MS = 4000;
const sharedGL = {
  renderer: null,
  env: null,
  shadowMap: null,
  viewers: new Set(),
  teardownTimer: null,
  onContextLost: null,
  onContextRestored: null,
};

function buildSharedEnvironment() {
  try {
    const pmremGenerator = new THREE.PMREMGenerator(sharedGL.renderer);
    sharedGL.env = pmremGenerator.fromScene(buildSoftboxEnvironment(), 0.04).texture;
    pmremGenerator.dispose();
  } catch (err) {
    console.warn('Peepz hero: environment map failed, falling back to basic lighting', err);
    sharedGL.env = null;
  }
}

function createSharedGL() {
  // Never added to the DOM -- it's only a render target for drawImage.
  const canvas = document.createElement('canvas');
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(1);
  // Matches the tool exactly -- without this the renderer stays at its
  // default NoToneMapping, and the PBR/iridescent math on the shimmer
  // faces (written assuming a filmic tone curve reshapes it afterward)
  // comes out dark/muddy rather than reading as metal.
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.setClearColor(0x000000, 0);
  renderer.setScissorTest(true);

  // Diagnostic, plus the one thing three.js doesn't redo on its own after a
  // restore: the PMREM environment is a render target, so its contents are
  // gone with the old context.
  sharedGL.onContextLost = (event) => {
    event.preventDefault();
    console.warn('Peepz hero: shared WebGL context lost');
  };
  sharedGL.onContextRestored = () => {
    console.warn('Peepz hero: shared WebGL context restored');
    buildSharedEnvironment();
    for (const viewer of sharedGL.viewers) {
      viewer.scene.environment = sharedGL.env;
      viewer.render();
    }
  };
  canvas.addEventListener('webglcontextlost', sharedGL.onContextLost);
  canvas.addEventListener('webglcontextrestored', sharedGL.onContextRestored);

  sharedGL.renderer = renderer;
  buildSharedEnvironment();
  sharedGL.shadowMap = createShadowTexture();
}

function destroySharedGL() {
  sharedGL.teardownTimer = null;
  if (sharedGL.viewers.size || !sharedGL.renderer) return;
  if (sharedGL.env) sharedGL.env.dispose();
  if (sharedGL.shadowMap) sharedGL.shadowMap.dispose();
  sharedGL.renderer.dispose();
  // Ask the browser to release the GPU context now rather than whenever the
  // detached canvas happens to be garbage collected -- with the listeners
  // off first, so this deliberate loss isn't logged as a real one.
  const canvas = sharedGL.renderer.domElement;
  canvas.removeEventListener('webglcontextlost', sharedGL.onContextLost);
  canvas.removeEventListener('webglcontextrestored', sharedGL.onContextRestored);
  sharedGL.renderer.forceContextLoss();
  sharedGL.renderer = null;
  sharedGL.env = null;
  sharedGL.shadowMap = null;
}

function acquireSharedGL(viewer) {
  if (sharedGL.teardownTimer !== null) {
    clearTimeout(sharedGL.teardownTimer);
    sharedGL.teardownTimer = null;
  }
  if (!sharedGL.renderer) createSharedGL();
  sharedGL.viewers.add(viewer);
  return sharedGL;
}

function releaseSharedGL(viewer) {
  sharedGL.viewers.delete(viewer);
  if (!sharedGL.viewers.size && sharedGL.teardownTimer === null) {
    sharedGL.teardownTimer = setTimeout(destroySharedGL, GL_IDLE_TEARDOWN_MS);
  }
}

// THREE.BoxGeometry builds each face as its own contiguous, equal-sized run of
// vertices with its own index group ([+x,-x,+y,-y,+z,-z]), so a subset of
// faces can be lifted out by slicing those runs -- materialIndex is kept as-is
// so setFaceMaterial's face numbers still line up.
function keepBoxFaces(geometry, faces) {
  const perFace = geometry.attributes.position.count / 6;
  const groups = faces.map((f) => geometry.groups.find((g) => g.materialIndex === f)).filter(Boolean);
  const attr = (name, size) => {
    const src = geometry.attributes[name];
    const out = new Float32Array(groups.length * perFace * size);
    groups.forEach((g, slot) => {
      const from = g.materialIndex * perFace * size;
      out.set(src.array.subarray(from, from + perFace * size), slot * perFace * size);
    });
    return new THREE.BufferAttribute(out, size);
  };
  const indexArray = new Uint16Array(groups.reduce((n, g) => n + g.count, 0));
  const kept = new THREE.BufferGeometry();
  let cursor = 0;
  groups.forEach((g, slot) => {
    const shift = (slot - g.materialIndex) * perFace;
    for (let i = 0; i < g.count; i++) indexArray[cursor + i] = geometry.index.array[g.start + i] + shift;
    kept.addGroup(cursor, g.count, g.materialIndex);
    cursor += g.count;
  });
  kept.setAttribute('position', attr('position', 3));
  kept.setAttribute('normal', attr('normal', 3));
  kept.setAttribute('uv', attr('uv', 2));
  kept.setIndex(new THREE.BufferAttribute(indexArray, 1));
  return kept;
}

/**
 * One self-contained rounded-box viewer in its own canvas/renderer/scene.
 * Rests still; setHoverTarget/clearHoverTarget drive a semi-turn parallax
 * while its column is hovered. Front/right faces get real PDF-driven
 * materials (with a metal/iridescent shimmer layer where the art has one)
 * via setFaceMaterial once loaded; the rest stay flat placeholder colors
 * until their own art exists.
 */
class BoxViewer {
  // `visibleFaces` (BoxGeometry face indices, [+x,-x,+y,-y,+z,-z]) drops every
  // other face from the geometry entirely. The hero only ever shows the front
  // (4), right (0) and lid (2) from its fixed 3/4 camera -- left, back and
  // bottom are always backface-culled anyway, but still cost a draw call and
  // vertex work each frame -- while the character section's turntable needs all six.
  constructor(canvas, { color, visibleFaces = null }) {
    this.canvas = canvas;
    // The shared context renders each box offscreen and this canvas just
    // displays the copy (see render()), so it's a plain 2D canvas -- no WebGL
    // context of its own to create, lose, or hold memory for.
    this._ctx = canvas.getContext('2d');
    if (!this._ctx) throw new Error('BoxViewer: canvas has no 2d context');
    this._active = false; // hover: bumps this box's drawing buffer to full device pixel ratio (see _setActive)
    this._devW = 0;
    this._devH = 0;
    this._disposed = false;

    const gl = acquireSharedGL(this);
    this._gl = gl;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(38, 1, 0.1, 50);
    // Viewing direction only -- resize() picks the actual distance each time
    // so the box (and its bounding sphere) always fits the frustum, however
    // narrow or short the container is. A fixed distance/FOV combo that only
    // happened to fit one particular canvas shape was exactly the bug that
    // clipped the top of every box (on desktop too, just less noticeably).
    // Mostly face-on with a modest turn and minimal downward tilt -- the
    // previous steep, angled-down direction was pointed at exactly the
    // wrong part of the environment for the shimmer faces to catch any
    // light, reading as flat dark regardless of material tuning.
    this.cameraDir = new THREE.Vector3(1.1, 0.45, 2.7).normalize();

    // No ambient/hemisphere/directional lights, on purpose -- matches the
    // tool exactly. All shading, on both the flat and metal/iridescent faces,
    // comes from the shared environment map instead: a flat added fill light
    // was actively drowning out the iridescence's subtle hue shifts by
    // raising the scene's overall brightness/lowering contrast, and a
    // directional light puts one hard sweeping hotspot on the metal. The
    // environment itself is baked ONCE for every box (see createSharedGL).
    // Some mobile WebGL implementations (seen on iOS Safari) can't build it
    // (no float/half-float render targets for PMREMGenerator) -- then every
    // box falls back to plain lights (flatter, no reflections) instead of
    // going blank.
    if (gl.env) {
      this.scene.environment = gl.env;
    } else {
      this._addFallbackLights();
    }

    const lidColor = mix(color, '#ffffff', 0.22);
    const baseMaterial = new THREE.MeshStandardMaterial({ color, roughness: 0.85 });
    const lidMaterial = new THREE.MeshStandardMaterial({ color: lidColor, roughness: 0.85 });
    // BoxGeometry face order: [+x, -x, +y, -y, +z, -z] -- index 2 is +Y (top).
    const materials = [baseMaterial, baseMaterial, lidMaterial, baseMaterial, baseMaterial, baseMaterial];

    // Box dims are in "cm / 10" units (height 1.5 = 15cm, matching the
    // tool's default box) -- so a corner radius given in real cm (0.105cm,
    // matching the tool's own cm-based UI) needs the same /10 conversion:
    // 0.105cm -> 0.0105 units.
    const CM_TO_UNITS = 0.1;
    let geometry = createRoundedBoxGeometry(1.1, 1.5, 0.75, 0.105 * CM_TO_UNITS, 12);
    // Measured on the FULL box, before any faces are dropped, so the camera
    // framing below is identical either way.
    geometry.computeBoundingSphere();
    // Local-space radius (geometry isn't translated, only the mesh is), so
    // this stays correct regardless of box.position.
    this.boundingRadius = geometry.boundingSphere.radius;
    if (visibleFaces) {
      const full = geometry;
      geometry = keepBoxFaces(full, visibleFaces);
      full.dispose();
    }
    this.box = new THREE.Mesh(geometry, materials);
    this.boxCenterY = 0.75;
    this.box.position.y = this.boxCenterY;
    this.scene.add(this.box);

    this.shadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1.8, 1.8),
      // The shadow texture itself is shared (one upload for every box); the
      // material is per-box because each box fades/scales its own shadow.
      new THREE.MeshBasicMaterial({ map: gl.shadowMap, transparent: true, depthWrite: false })
    );
    this.shadow.rotation.x = -Math.PI / 2;
    this.shadow.position.y = 0.01;
    this.scene.add(this.shadow);

    // Rests at rotation (0,0) and scale 1. A hover parallax turn plus a
    // slight lift are the box's own interactivity -- setHoverTarget/
    // clearHoverTarget below drive these toward a target; the render loop
    // only actually runs while easing toward it, stopping once settled so
    // an idle box still costs nothing (matches the "boxes shouldn't
    // rotate" baseline when nobody's hovering). The "lift" is done by
    // scaling the box mesh itself, in 3D, rather than the canvas element
    // via CSS -- a CSS transform on the canvas would scale the shadow
    // right along with the box, which is backwards: lifting an object
    // off the ground should shrink (and soften) its shadow, not grow it.
    this.targetRotX = 0;
    this.targetRotY = 0;
    this.targetLift = 0; // 0..1
    this.lift = 0;
    this.isVisible = true;
    this._rafId = null;
    this._autoRotateRafId = null;
    this._autoRotateSpeed = 0;

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        this.isVisible = entries[0].isIntersecting;
        if (this.isVisible) this.render();
      },
      { threshold: 0.01 }
    );
    this.intersectionObserver.observe(canvas);

    this._frame = this._frame.bind(this);
  }

  _addFallbackLights() {
    this.scene.add(new THREE.AmbientLight(0xffffff, 1.2));
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(2, 3, 4);
    this.scene.add(key);
  }

  // nx/ny in -0.5..0.5, relative to this box's own column.
  setHoverTarget(nx, ny) {
    const maxTurnY = 0.35; // rad (~20deg) at the column's edge -- a semi-turn, not a full spin
    const maxTurnX = 0.15; // rad (~8.6deg) -- kept smaller so it doesn't look like it's toppling
    // nx/ny only reach +-0.5, not +-1, so double them to actually hit
    // maxTurnY/X at the column edge instead of just half of it.
    this.targetRotY = nx * 2 * maxTurnY;
    this.targetRotX = -ny * 2 * maxTurnX;
    this.targetLift = 1;
    this._setActive(true);
    this._ensureAnimating();
  }

  clearHoverTarget() {
    this.targetRotX = 0;
    this.targetRotY = 0;
    this.targetLift = 0;
    this._setActive(false);
    this._ensureAnimating();
  }

  // Full device pixel ratio only while actually hovered, 1x otherwise, so
  // the two idle boxes hold a smaller drawing buffer (see resize()).
  _setActive(active) {
    if (this._active === active) return;
    this._active = active;
    this.resize();
  }

  // Mobile's stand-in for the hover turn above (see PeepzHeroComponent,
  // which drives this from how much of the box's canvas has scrolled into
  // view, 0..1). Doesn't touch lift -- this is a turn, not a hover.
  //
  // cameraDir (1.1, 0.45, 2.7) sits at a fixed ~22deg azimuth off the box's
  // own front (+Z) face, which is what gives the resting rotation.y=0 pose
  // its "3/4 view" look in the first place. Positive rotation cancels that
  // azimuth (more front-facing/square-on); negative compounds with it
  // (more turned than resting).
  setScrollTurn(progress) {
    const START_ROT_Y = 0.3; // rad -- most of the way to square-on/front-facing
    const DEEP_TURN_ROT_Y = -0.9; // rad -- a very deep turn past the resting pose
    const t = Math.min(1, Math.max(0, progress));
    this.targetRotX = 0;
    // progress=0 (box just arriving, bottom of viewport) starts front-facing,
    // sweeping into a deep turn as it scrolls further into view.
    this.targetRotY = START_ROT_Y + (DEEP_TURN_ROT_Y - START_ROT_Y) * t;
    this._ensureAnimating();
  }

  // Direct manual control for drag-to-rotate (see PeepzCharacterSectionComponent)
  // -- sets rotation immediately rather than easing toward a target like
  // setHoverTarget/setScrollTurn above, since a dragged box should track the
  // pointer exactly, with no lag. Also updates targetRotX/Y to match, so
  // nothing else later eases it back to a stale pre-drag target.
  setManualRotation(rotY, rotX) {
    this.targetRotY = rotY;
    this.targetRotX = rotX;
    this.box.rotation.y = rotY;
    this.box.rotation.x = rotX;
    this.render();
  }

  // Continuous turntable spin for PeepzCharacterSectionComponent -- its own
  // rAF loop rather than the target+easing system above (setHoverTarget/
  // setScrollTurn), since that system is built to settle at a fixed target
  // and stop, and a spin never settles. Respects the same isVisible flag
  // as the render-on-demand loop elsewhere, so it doesn't spend frames
  // rotating (and rendering) a box that's currently scrolled out of view.
  startAutoRotate(speed = 0.005) {
    if (REDUCED_MOTION || this._autoRotateRafId !== null) return;
    this._autoRotateSpeed = speed;
    const tick = () => {
      if (this._disposed) {
        this._autoRotateRafId = null;
        return;
      }
      if (this.isVisible) {
        this.box.rotation.y += this._autoRotateSpeed;
        this.targetRotY = this.box.rotation.y;
        this.render();
      }
      this._autoRotateRafId = requestAnimationFrame(tick);
    };
    this._autoRotateRafId = requestAnimationFrame(tick);
  }

  stopAutoRotate() {
    if (this._autoRotateRafId !== null) {
      cancelAnimationFrame(this._autoRotateRafId);
      this._autoRotateRafId = null;
    }
  }

  _applyLift(lift) {
    const LIFT_SCALE = 0.12; // box grows up to 12% at full lift
    const LIFT_HEIGHT = 0.18; // and rises this many world units off the "ground"
    const SHADOW_SHRINK = 0.3; // shadow shrinks to 70% of its size at full lift
    const SHADOW_FADE = 0.35; // and fades to 65% opacity
    this.box.scale.setScalar(1 + lift * LIFT_SCALE);
    // Scale alone doesn't read as "lifting" -- scaling around the box's own
    // center pushes its bottom face down as much as its top goes up, so
    // without also moving it, the box never actually separates from the
    // shadow (if anything it sinks further into it). Translating up is
    // what actually creates the gap.
    this.box.position.y = this.boxCenterY + lift * LIFT_HEIGHT;
    // Scaling the plane mesh (not moving it) keeps the shadow anchored at
    // the same spot on the "ground" while it shrinks -- lifting an object
    // off a surface shrinks (and softens) its shadow, it doesn't move it.
    this.shadow.scale.setScalar(1 - lift * SHADOW_SHRINK);
    this.shadow.material.opacity = 1 - lift * SHADOW_FADE;
  }

  _ensureAnimating() {
    if (REDUCED_MOTION) {
      this.box.rotation.set(this.targetRotX, this.targetRotY, 0);
      this.lift = this.targetLift;
      this._applyLift(this.lift);
      this.render();
      return;
    }
    if (this._rafId === null) this._rafId = requestAnimationFrame(this._frame);
  }

  _frame() {
    this._rafId = null;
    if (!this.isVisible) return;
    const EASE = 0.12;
    const SETTLE_EPSILON = 0.0005;
    this.box.rotation.x += (this.targetRotX - this.box.rotation.x) * EASE;
    this.box.rotation.y += (this.targetRotY - this.box.rotation.y) * EASE;
    this.lift += (this.targetLift - this.lift) * EASE;
    this._applyLift(this.lift);
    this.render();

    const settled =
      Math.abs(this.targetRotX - this.box.rotation.x) < SETTLE_EPSILON &&
      Math.abs(this.targetRotY - this.box.rotation.y) < SETTLE_EPSILON &&
      Math.abs(this.targetLift - this.lift) < SETTLE_EPSILON;
    if (settled) {
      this.box.rotation.set(this.targetRotX, this.targetRotY, 0);
      this.lift = this.targetLift;
      this._applyLift(this.lift);
      this.render();
    } else {
      this._rafId = requestAnimationFrame(this._frame);
    }
  }

  resize() {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    // This canvas's own backing store: the box's CSS size x pixel ratio.
    // Assigning width/height clears a 2D canvas, which is fine -- every
    // caller renders straight after.
    const ratio = this._active ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    const devW = Math.max(1, Math.round(rect.width * ratio));
    const devH = Math.max(1, Math.round(rect.height * ratio));
    if (this.canvas.width !== devW || this.canvas.height !== devH) {
      this.canvas.width = devW;
      this.canvas.height = devH;
    }
    this._devW = devW;
    this._devH = devH;
    const aspect = rect.width / rect.height;
    this.camera.aspect = aspect;

    // Dolly the camera along its fixed viewing direction to whatever
    // distance makes the box's bounding sphere fit inside BOTH the
    // vertical and horizontal frustum for this container's aspect ratio
    // -- a wide/short container needs the vertical fit distance, a
    // narrow/tall one (most phones, stacked columns) needs the horizontal
    // one. Padding leaves a small margin instead of touching the edges.
    const PADDING = 1.05;
    const halfVFov = (this.camera.fov * Math.PI) / 180 / 2;
    const halfV = Math.tan(halfVFov);
    const halfH = halfV * aspect;
    const distance = this.boundingRadius * Math.max(1 / halfV, 1 / halfH) * PADDING;

    this.camera.position.copy(this.cameraDir).multiplyScalar(distance);
    this.camera.position.y += this.boxCenterY;
    this.camera.lookAt(0, this.boxCenterY, 0);
    this.camera.updateProjectionMatrix();
    this.render();
  }

  // Renders into the shared context's drawing buffer, then copies just this
  // box's region onto its own canvas -- drawImage straight after render() in
  // the same task, so the shared context needs no preserveDrawingBuffer.
  // The shared buffer only ever GROWS to the biggest box seen (each box
  // draws into a sub-viewport of it) rather than being resized per box: with
  // one idle-size box and one hover-size box both animating, resizing on
  // every render would reallocate the multisampled buffer every frame.
  render() {
    // Guards against a rAF/ResizeObserver/IntersectionObserver callback that
    // was already queued before dispose() ran (mobile disposes a box the
    // instant it scrolls out of view -- see PeepzHeroComponent) from calling
    // into an already-released shared context.
    if (this._disposed || !this._devW) return;
    const renderer = this._gl.renderer;
    if (!renderer) return;
    const el = renderer.domElement;
    const w = this._devW;
    const h = this._devH;
    if (el.width < w || el.height < h) {
      renderer.setSize(Math.max(el.width, w), Math.max(el.height, h), false);
    }
    renderer.setViewport(0, 0, w, h);
    renderer.setScissor(0, 0, w, h);
    renderer.render(this.scene, this.camera);
    // WebGL's origin is bottom-left, so the sub-viewport above is the
    // bottom-left corner of the buffer -- bottom rows in canvas coordinates.
    this._ctx.clearRect(0, 0, w, h);
    this._ctx.drawImage(el, 0, el.height - h, w, h, 0, 0, w, h);
  }

  // Swaps one face's material (BoxGeometry order: [+x, -x, +y, -y, +z, -z],
  // so 0=right, 4=front) -- used once the face materials finish loading. The
  // material being replaced (a flat placeholder, or an older face material)
  // is disposed; `material` itself is left alone.
  //
  // The shimmer faces' MeshPhysicalMaterial (iridescence + clearcoat + three
  // maps + environment) is the heaviest shader in the scene, and a plain
  // render() compiles it synchronously on the main thread the first time it's
  // drawn -- a visible hitch on slower phones. compileAsync starts the
  // compile in the driver's background threads (KHR_parallel_shader_compile)
  // and resolves once it's ready, so the swap only paints then. Resolves
  // after that first paint, so callers can hold their loading state until
  // the art is actually on screen. (The program is shared by every box, so
  // only the first swap on the page pays for it.)
  async setFaceMaterial(index, material) {
    const old = this.box.material[index];
    this.box.material[index] = material;
    if (old && old !== material) disposeMaterial(old);
    try {
      await this._gl.renderer?.compileAsync(this.scene, this.camera);
    } catch {
      /* worst case it compiles synchronously in render(), as it always used to */
    }
    this.render();
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    if (this._rafId !== null) cancelAnimationFrame(this._rafId);
    this.stopAutoRotate();
    this.resizeObserver.disconnect();
    this.intersectionObserver.disconnect();
    this.box.geometry.dispose();
    for (const material of this.box.material) disposeMaterial(material);
    this.shadow.geometry.dispose();
    // Not shadow.material.map -- that texture (and the environment) belong
    // to the shared context, not to this box.
    this.shadow.material.dispose();
    releaseSharedGL(this);
  }
}

/**
 * The foil bag used by PeepzCharacterSectionComponent's "open box" flow --
 * a puffed rounded-box mesh (see bulgePouchFaces) printed with the shared
 * bag pattern, with metalness/iridescence for a foil look, and the same
 * shimmer-PDF pipeline as BoxViewer's faces for an eventual holographic
 * patch once that art exists. Static (no hover/lift/rotation) -- the tear
 * seam and fall-away animation are driven entirely by CSS/clip-path from
 * PeepzCharacterSectionComponent, not by this viewer.
 */
class BagViewer {
  constructor(canvas) {
    this.canvas = canvas;
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, preserveDrawingBuffer: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    // The ambient light below was originally strong enough (1.6) to
    // dominate over the directional lights combined -- ambient lights every
    // point on the mesh equally regardless of its normal, so that washed
    // out the very shading gradient that makes the bulge read as sculpted
    // rather than flat, and it doesn't push the tonemapped result any
    // closer to actual white the way a directional highlight does. A
    // little more exposure is what gets bright areas to actually read as
    // white after the ACES curve, rather than a slightly muddy grey.
    this.renderer.toneMappingExposure = 1.15;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(34, 1, 0.1, 50);
    this.cameraDir = new THREE.Vector3(0.55, 0.15, 1.6).normalize();

    // Plain lights, not a second PMREM-baked environment -- this renderer
    // is created on top of the box's own already-live WebGL context (see
    // BoxViewer), and baking a whole cubemap scene a second time here was
    // a real cause of that first context losing its own contents (the
    // box mesh going invisible while its shadow plane, part of the same
    // scene/renderer, kept rendering fine -- a GPU/driver resource-
    // pressure symptom, not a CSS or stacking issue). Cheaper, and the
    // bag doesn't need the box's full lighting fidelity anyway.
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const key = new THREE.DirectionalLight(0xffffff, 2.1);
    key.position.set(2, 3, 4);
    this.scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.7);
    fill.position.set(-2, 1, 2);
    this.scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffffff, 0.5);
    rim.position.set(-1.5, -1, -2);
    this.scene.add(rim);

    // Sharp corners, straight edges (radius 0 -- createRoundedBoxGeometry
    // no-ops below ~0.0001), a flat foil-bag silhouette. Not welded -- see
    // bulgePouchFaces' own comment for why that keeps the rim crisp. A
    // higher segment count (28) than the box needs on its own -- the
    // lobes' falloff bands are narrow, so there need to be enough vertices
    // across them for the transition to still look smooth rather than
    // faceted. Depth (0.002) is as close to a true zero-thickness rim as
    // actually works -- a literal 0 collapses the front/back faces onto
    // the same plane and produces degenerate, garbage normals on the
    // (zero-height) side walls, which rendered as visible streak
    // artifacts; this is visually indistinguishable from zero without
    // that. bulgePouchFaces' own push is what gives the bag any real
    // thickness.
    let geometry = createRoundedBoxGeometry(1.15, 1.4, 0.002, 0, 28);
    bulgePouchFaces(geometry, 1.15, 1.4, 0.22);
    geometry.computeBoundingSphere();
    // metalness was 0.2, but with no environment map for it to actually
    // reflect (see the comment above), it was only ever taking away from
    // the diffuse white base color, never giving back an equivalent
    // reflection -- a straightforwardly duller, greyer result for no
    // visual payoff. Dropped near-zero, and a little clearcoat added
    // instead: a clearcoat layer picks up crisp specular highlights
    // straight from the directional lights without needing any
    // environment map, which is what a plain metalness value can't do here.
    const material = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      metalness: 0.05,
      roughness: 0.55,
      clearcoat: 0.3,
      clearcoatRoughness: 0.25,
      envMapIntensity: 0.6,
    });
    this.bag = new THREE.Mesh(geometry, material);
    this.scene.add(this.bag);
    // The bag's own flat half-width/half-height (not its bounding SPHERE,
    // which is calibrated to the diagonal corner-to-center distance -- for
    // a wide, flat mesh like this one that's a much bigger radius than
    // either the width or height alone, so fitting the camera to it left
    // the bag occupying only about half the frame on each axis, with a
    // huge unused margin. resize() below fits to these instead.
    this.bagHalfW = 1.15 / 2;
    this.bagHalfH = 1.4 / 2;

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();

    const patternUrl = canvas.dataset.patternUrl;
    if (patternUrl) {
      loadPatternTexture(patternUrl, 1024)
        .then((texture) => {
          if (this._disposed || this._shimmerApplied) {
            texture.dispose();
            return;
          }
          this.bag.material.map = texture;
          this.bag.material.needsUpdate = true;
          this.render();
        })
        .catch(() => {
          /* keep the flat foil-color placeholder */
        });
    }

    const shimmerUrl = canvas.dataset.shimmerPdfUrl;
    if (shimmerUrl) {
      buildPdfFaceMaterial(shimmerUrl, 'shimmer', '#d9d9de')
        .then((material) => {
          // buildPdfFaceMaterial always returns a material (a flat
          // MeshStandardMaterial fallback if the PDF had no `shimmer`
          // layer) -- only treat it as a real holographic hit if it came
          // back as the physical/iridescent variant.
          if (this._disposed || !(material instanceof THREE.MeshPhysicalMaterial)) {
            disposeMaterial(material);
            return;
          }
          this._shimmerApplied = true;
          const old = this.bag.material;
          this.bag.material = material;
          disposeMaterial(old);
          this.render();
        })
        .catch(() => {
          /* PDF not dropped in yet, or missing the shimmer layer -- keep the pattern texture */
        });
    }
  }

  resize() {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    this.renderer.setSize(rect.width, rect.height, false);
    const aspect = rect.width / rect.height;
    this.camera.aspect = aspect;
    const PADDING = 1.15;
    const halfV = Math.tan((this.camera.fov * Math.PI) / 180 / 2);
    const halfH = halfV * aspect;
    // Fits to the bag's actual half-width/half-height, not boundingRadius
    // -- see bagHalfW/bagHalfH's own comment. This is also why the DOM
    // overlays (character clip-path, tear line/handle) can be positioned
    // with plain CSS percentages of the container and land on the actual
    // bag edges: the bag now fills that container edge-to-edge (minus
    // PADDING's small margin) instead of occupying an arbitrary, much
    // smaller fraction of it.
    const distance = Math.max(this.bagHalfW / halfH, this.bagHalfH / halfV) * PADDING;
    this.camera.position.copy(this.cameraDir).multiplyScalar(distance);
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
    this.render();
  }

  render() {
    if (this._disposed) return;
    this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    this._disposed = true;
    this.resizeObserver.disconnect();
    this.bag.geometry.dispose();
    disposeMaterial(this.bag.material);
    if (this.scene.environment) this.scene.environment.dispose();
    this.renderer.dispose();
  }
}

// Renders one BagViewer frame to a PNG data URL, then immediately disposes
// the renderer -- rather than keeping the bag's WebGL context alive for the
// whole open-box interaction (shake, fly-out, drag, tear, fall-away: several
// seconds with two live contexts, the box's and the bag's, at once). The bag
// was already static (no rotation/hover/interactivity of its own), so a
// snapshot loses nothing visually; it just shrinks the window where two
// contexts coexist from "the whole interaction" down to under a second,
// which is the actual mitigation for the box's own WebGL context going
// blank partway through (its shadow plane, part of the same scene/renderer,
// kept drawing fine throughout -- a GPU/driver resource-pressure symptom
// from the second context, not a CSS or logic bug in the box itself).
// Every character section on the page uses the exact same bag art
// (patternUrl/shimmerUrl are fixed literals in the template, not
// per-character), so without this, opening a SECOND or THIRD character's
// box repeated the whole expensive pipeline -- a new WebGL context, the
// pattern texture load, and (if the shimmer PDF exists) a full
// render-twice-diff PDF pass -- to produce a pixel-identical result. This
// is exactly the kind of thing worth trimming given the memory pressure
// "Open Box" already puts on iOS. Keyed by the URL pair so a future
// per-character bag skin wouldn't silently reuse the wrong art.
const bagSnapshotCache = new Map();

function renderBagSnapshot(patternUrl, shimmerUrl) {
  const cacheKey = `${patternUrl || ''}|${shimmerUrl || ''}`;
  const cached = bagSnapshotCache.get(cacheKey);
  if (cached) return cached;
  const promise = renderBagSnapshotUncached(patternUrl, shimmerUrl).catch((err) => {
    // Don't poison the cache with a failed attempt -- a transient error
    // (e.g. a network blip) shouldn't permanently block every other
    // section on the page from ever getting a bag snapshot.
    bagSnapshotCache.delete(cacheKey);
    throw err;
  });
  bagSnapshotCache.set(cacheKey, promise);
  return promise;
}

function renderBagSnapshotUncached(patternUrl, shimmerUrl) {
  const offscreen = document.createElement('canvas');
  offscreen.width = 640;
  offscreen.height = 780; // fixed, crisp resolution -- matches the bag's ~1.15:1.4 aspect
  offscreen.style.cssText = 'position:fixed; left:-9999px; top:0; width:640px; height:780px;';
  offscreen.dataset.patternUrl = patternUrl || '';
  offscreen.dataset.shimmerPdfUrl = shimmerUrl || '';
  document.body.appendChild(offscreen);

  let viewer;
  try {
    viewer = new BagViewer(offscreen);
  } catch (err) {
    offscreen.remove();
    return Promise.reject(err);
  }

  // The pattern texture (a local SVG composited to canvas) loads fast; the
  // shimmer PDF attempt may still be in flight (today it 404s quickly,
  // since that file hasn't been dropped in yet) -- not worth blocking the
  // snapshot on indefinitely, this can be revisited once real shimmer art
  // exists.
  return new Promise((resolve) => setTimeout(resolve, 150)).then(() => {
    viewer.render();
    const dataUrl = offscreen.toDataURL('image/png');
    viewer.dispose();
    offscreen.remove();
    return dataUrl;
  });
}

class PeepzHeroComponent extends HTMLElement {
  connectedCallback() {
    const canvases = Array.from(this.querySelectorAll('[data-peepz-box-canvas]'));
    // Index-aligned with `canvases`/`this.columns`, not built up with
    // push/flatMap -- entries start null and fill in as each box actually
    // loads (see _initBoxViewer below), in whatever order that happens.
    this.viewers = new Array(canvases.length).fill(null);

    // All three boxes used to construct immediately on page load: three
    // WebGL renderers, three PMREM-baked environments, and three PDF->canvas
    // texture builds all spinning up at once -- a plausible match for iOS's
    // "A Problem Repeatedly Occurred" GPU-process crash. Deferring
    // construction until a canvas scrolls near-into-view fixed page load,
    // but confirmed on-device: merely deferring isn't enough on mobile --
    // the *first* box's WebGL context was never being torn down once
    // scrolled away, so scrolling to the second box still meant two live
    // renderers + environments + texture sets at once, and that alone was
    // enough to crash. On mobile (stacked full-height columns, one box
    // realistically in view at a time) each box is now disposed the moment
    // it scrolls out and reconstructed if scrolled back to, capping this at
    // one live WebGL context regardless of how far down the page you go.
    // Desktop shows all three boxes side by side in one viewport already --
    // there's no "off-screen" to unload there, so it keeps the simpler
    // load-once-and-keep behavior.
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    // Front-to-turned reveal as a box scrolls into view (see
    // BoxViewer.setScrollTurn) -- mobile's replacement for the desktop
    // hover turn, since there's no pointer to drive it on touch. Driven
    // directly off scroll position (rAF-batched, like _onPointerMove
    // below), not the exclusivity observer's intersectionRatio further
    // down -- that only updates at its 21 threshold crossings, which read
    // as steppy rather than actually tracking the scroll.
    let updateScrollTurn = () => {};
    if (isMobile) {
      let scrollRafId = null;
      updateScrollTurn = () => {
        scrollRafId = null;
        const viewer = this.viewers.find((v) => v);
        if (!viewer) return;
        const rect = viewer.canvas.getBoundingClientRect();
        // Spans the canvas's *entire* transit through the viewport (0 when
        // its top edge first touches the bottom of the viewport, 1 when its
        // bottom edge clears the top) rather than being centered on the
        // viewport's middle -- a center-relative version spent most of its
        // range while the box was still off toward the top/bottom edges,
        // barely visible, so by the time it was actually centered and being
        // looked at, the turn had mostly already happened and stopped.
        // This instead plays the turn out gradually across the box's whole
        // time on screen.
        const progress = Math.min(1, Math.max(0, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
        viewer.setScrollTurn(progress);
      };
      this._onMobileScroll = () => {
        if (scrollRafId === null) scrollRafId = requestAnimationFrame(updateScrollTurn);
      };
      window.addEventListener('scroll', this._onMobileScroll, { passive: true });
    }

    if ('IntersectionObserver' in window) {
      // Mobile columns are sized to the space actually left below the
      // sticky header (100vh - header height), but IntersectionObserver's
      // root is the *whole* layout viewport, header included -- so a
      // column exactly filling its own post-header space still always
      // leaves the observer's larger window with room for a neighboring
      // column to poke in too. A plain isIntersecting boolean at 0
      // rootMargin still let two boxes both read as "intersecting" through
      // most of the scroll between them. Tracking intersectionRatio and
      // keeping only the single most-visible canvas loaded (finely, via a
      // dense threshold list, not just enter/exit) makes "one live box at a
      // time" an actual invariant instead of a rootMargin approximation.
      const ratios = new Array(canvases.length).fill(0);
      const applyMobileExclusivity = () => {
        let bestIndex = -1;
        let bestRatio = 0;
        ratios.forEach((r, i) => {
          if (r > bestRatio) {
            bestRatio = r;
            bestIndex = i;
          }
        });
        canvases.forEach((canvas, i) => {
          if (i === bestIndex) this._initBoxViewer(canvas, i);
          else this._disposeBoxViewer(i);
        });
        // Sets the freshly-loaded box's rotation to match however far
        // scrolled it already is, rather than leaving it at the Three.js
        // default (0,0,0) until the next actual scroll event fires.
        updateScrollTurn();
      };

      const lazyObserver = new IntersectionObserver(
        (entries) => {
          if (isMobile) {
            for (const entry of entries) ratios[canvases.indexOf(entry.target)] = entry.intersectionRatio;
            applyMobileExclusivity();
            return;
          }
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            this._initBoxViewer(entry.target, canvases.indexOf(entry.target));
            lazyObserver.unobserve(entry.target);
          }
        },
        {
          rootMargin: isMobile ? '0px' : '200px',
          threshold: isMobile ? Array.from({ length: 21 }, (_, i) => i / 20) : undefined,
        }
      );
      canvases.forEach((canvas) => lazyObserver.observe(canvas));
      this._lazyBoxObserver = lazyObserver;

      // Desktop shows all three boxes together, so unlike mobile there's
      // no per-box "off-screen" to exploit -- but the *whole hero* still
      // goes off-screen once the page scrolls past it. Watching the
      // component itself (not the individual canvases, which the observer
      // above already stops watching once loaded) disposes all three
      // WebGL contexts together the moment none of the hero is visible,
      // and reconstructs them (re-fetching and re-masking PDFs, same cost
      // as mobile's per-box reload) the moment it scrolls back into view.
      // A page with more of these lower down (see the "Se mere" links)
      // multiplies this cost across every one of them if they all stay
      // resident forever, so this is the same "one screenful's worth of
      // WebGL alive at a time" principle mobile already uses, just scoped
      // to a whole section instead of a single box.
      if (!isMobile) {
        // Debounced: a freshly-observed element's very first callback (or
        // one during initial layout/font/sticky-header settling) can
        // report a transient isIntersecting:false before things settle,
        // even though the hero is genuinely on screen -- disposing
        // immediately on that would tear down boxes the other observer
        // just finished constructing. Waiting a beat, and canceling if
        // visibility comes back before it fires, keeps a momentary blip
        // from destroying already-loaded boxes while still reclaiming
        // memory shortly after a real, sustained scroll-away.
        let disposeTimer = null;
        const heroObserver = new IntersectionObserver(
          (entries) => {
            const isVisible = entries[0].isIntersecting;
            if (isVisible) {
              if (disposeTimer !== null) {
                clearTimeout(disposeTimer);
                disposeTimer = null;
              }
              canvases.forEach((canvas, i) => this._initBoxViewer(canvas, i));
            } else if (disposeTimer === null) {
              disposeTimer = setTimeout(() => {
                disposeTimer = null;
                canvases.forEach((canvas, i) => this._disposeBoxViewer(i));
              }, 400);
            }
          },
          { threshold: 0 }
        );
        heroObserver.observe(this);
        this._heroVisibilityObserver = heroObserver;
        this._heroDisposeTimerClear = () => {
          if (disposeTimer !== null) clearTimeout(disposeTimer);
        };
      }
    } else {
      canvases.forEach((canvas, i) => this._initBoxViewer(canvas, i));
    }

    this.columns = Array.from(this.querySelectorAll('[data-peepz-column]'));
    this.logo = this.querySelector('[data-peepz-logo]');

    this.reduceMotion = REDUCED_MOTION;
    this._pendingEvent = null;
    this._rafScheduled = false;
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerLeave = this._onPointerLeave.bind(this);
    this._applyPointerMove = this._applyPointerMove.bind(this);
    this.addEventListener('pointermove', this._onPointerMove);
    this.addEventListener('pointerleave', this._onPointerLeave);
  }

  // Per-canvas try/catch: a single box's WebGL/Three.js setup throwing
  // (e.g. an unsupported capability on some device) used to abort the whole
  // batch that constructed all boxes together, silently dropping every box
  // it hadn't gotten to yet.
  _initBoxViewer(canvas, index) {
    if (this.viewers[index]) return;
    try {
      // Only front (4), right (0) and lid (2) ever face the hero's camera.
      const viewer = new BoxViewer(canvas, { color: canvas.dataset.boxColor, visibleFaces: [4, 0, 2] });
      this.viewers[index] = viewer;
      this._loadBoxFaceArt(canvas, viewer, index);
    } catch (err) {
      console.warn('Peepz hero: box viewer failed to initialize', canvas, err);
    }
  }

  // Mobile-only (see connectedCallback): tears a box's WebGL context down
  // completely when it scrolls out of view, rather than just pausing its
  // render loop, so memory doesn't accumulate across however many boxes
  // have been scrolled past. _initBoxViewer reconstructs it from scratch if
  // scrolled back to.
  _disposeBoxViewer(index) {
    const viewer = this.viewers[index];
    if (!viewer) return;
    viewer.dispose();
    this.viewers[index] = null;
  }

  // Each box loads its own front/right face art independently, from that
  // canvas's own data-front-pdf-url/data-right-pdf-url -- a character with
  // no file yet (or a broken one) just 404s quietly and that one face
  // keeps its flat placeholder color, rather than failing the whole box or
  // blocking the other two characters' art from loading.
  async _loadBoxFaceArt(canvas, viewer, index) {
    const jobs = [
      { side: 'front', faceIndex: 4 }, // BoxGeometry [+x,-x,+y,-y,+z,-z] -- 4 = front
      { side: 'right', faceIndex: 0 }, // 0 = right
    ].filter((job) => canvas.dataset[`${job.side}TexUrl`] || canvas.dataset[`${job.side}PdfUrl`]);
    if (!jobs.length) return;

    const wrap = canvas.closest('.peepz-hero__box-canvas-wrap');
    if (wrap) wrap.classList.add('is-loading');
    try {
      await Promise.all(
        jobs.map(async (job) => {
          try {
            const material = await loadFaceMaterial(canvas, job.side);
            if (!material) return;
            // On mobile this box's viewer may have been disposed (scrolled
            // away) or even disposed-and-reconstructed as a new instance
            // (scrolled away and back) while this fetch was in flight --
            // applying a loaded material to anything other than the
            // still-current viewer at this index would touch a dead
            // renderer or silently overwrite a fresher one's face art.
            if (!this.isConnected || this.viewers[index] !== viewer) {
              disposeMaterial(material);
              return;
            }
            await viewer.setFaceMaterial(job.faceIndex, material);
          } catch (err) {
            console.warn('Peepz hero: face art failed to load', job.side, err);
          }
        })
      );
    } finally {
      if (wrap) wrap.classList.remove('is-loading');
    }
  }

  // Raw `pointermove` can fire well over 60 times/sec -- writing several
  // inline styles (each one forcing a style recalc on a ~650-path inline
  // SVG) synchronously on every single one of those is what made the hover
  // effect feel glitchy/janky. Coalescing to one DOM update per animation
  // frame, and doing every layout READ (getBoundingClientRect) before any
  // style WRITE within that update, avoids both the excess work and
  // layout-thrashing from interleaving reads and writes.
  _onPointerMove(event) {
    this._pendingEvent = event;
    if (this._rafScheduled) return;
    this._rafScheduled = true;
    requestAnimationFrame(this._applyPointerMove);
  }

  _applyPointerMove() {
    this._rafScheduled = false;
    const event = this._pendingEvent;
    if (!event || !this.logo) return;

    const rect = this.getBoundingClientRect();
    const columnRects = this.columns.map((column) => column.getBoundingClientRect());
    const nx = (event.clientX - rect.left) / rect.width - 0.5; // -0.5..0.5
    const ny = (event.clientY - rect.top) / rect.height - 0.5;

    const hoveredIndex = columnRects.findIndex(
      (r) => event.clientX >= r.left && event.clientX <= r.right && event.clientY >= r.top && event.clientY <= r.bottom
    );
    const hovered = hoveredIndex === -1 ? null : this.columns[hoveredIndex];

    if (!this.reduceMotion) this._applyTilt(nx, ny);

    if (hovered) {
      this.logo.style.setProperty('--logo-bg-color', hovered.dataset.logoBg);
      this.logo.style.setProperty('--halftone-color', hovered.dataset.halftone);
    } else {
      this.logo.style.removeProperty('--logo-bg-color');
      this.logo.style.removeProperty('--halftone-color');
    }

    if (!this.reduceMotion) {
      this.viewers.forEach((viewer, i) => {
        if (!viewer) return; // not lazy-loaded (in view) yet
        if (i === hoveredIndex) {
          const r = columnRects[i];
          const boxNx = (event.clientX - r.left) / r.width - 0.5;
          const boxNy = (event.clientY - r.top) / r.height - 0.5;
          viewer.setHoverTarget(boxNx, boxNy);
        } else {
          viewer.clearHoverTarget();
        }
      });
    }
  }

  // nx/ny in -0.5..0.5, derived from the mouse position in _applyPointerMove.
  _applyTilt(nx, ny) {
    if (!this.logo) return;
    const maxTilt = 18; // degrees -- noticeable but not as exaggerated as before
    this.logo.style.setProperty('--tilt-x', `${(-ny * maxTilt).toFixed(2)}deg`);
    this.logo.style.setProperty('--tilt-y', `${(nx * maxTilt).toFixed(2)}deg`);

    // Same input also drives the holograph foil's gradient -- an angle-
    // dependent sheen is the 2D analog of the box render's iridescence
    // shifting with camera angle. The idle CSS animation (see
    // .peepz-hero__holo / .is-active in the section styles) takes over
    // again once the pointer leaves.
    this.classList.add('is-active');
    this.logo.style.setProperty('--holo-x', `${(50 + nx * 70).toFixed(1)}%`);
    this.logo.style.setProperty('--holo-y', `${(50 + ny * 70).toFixed(1)}%`);
  }

  _onPointerLeave() {
    this._pendingEvent = null;
    for (const viewer of this.viewers) { if (viewer) viewer.clearHoverTarget(); }
    if (!this.logo) return;
    this.classList.remove('is-active');
    this.logo.style.setProperty('--tilt-x', '0deg');
    this.logo.style.setProperty('--tilt-y', '0deg');
    this.logo.style.removeProperty('--holo-x');
    this.logo.style.removeProperty('--holo-y');
    this.logo.style.removeProperty('--logo-bg-color');
    this.logo.style.removeProperty('--halftone-color');
  }

  disconnectedCallback() {
    if (this._lazyBoxObserver) this._lazyBoxObserver.disconnect();
    if (this._heroVisibilityObserver) this._heroVisibilityObserver.disconnect();
    if (this._heroDisposeTimerClear) this._heroDisposeTimerClear();
    if (this._onMobileScroll) window.removeEventListener('scroll', this._onMobileScroll);
    for (const viewer of this.viewers) { if (viewer) viewer.dispose(); }
    this.removeEventListener('pointermove', this._onPointerMove);
    this.removeEventListener('pointerleave', this._onPointerLeave);
  }
}

customElements.define('peepz-hero-component', PeepzHeroComponent);

const OPEN_BOX_TEAR_STEPS = 12;
const OPEN_BOX_TEAR_JITTER = 0.015; // fraction of bag height, per zigzag step -- softer/less spiky than an earlier 0.035
const OPEN_BOX_TOP_SEAM_INSET = 0.16; // how far down the torn-open gap reaches, as a fraction of bag height
const OPEN_BOX_SLOT_COUNT = 4;

/**
 * A single-box counterpart to PeepzHeroComponent, for the full-height
 * per-character sections lower down the page (see peepz-character-section
 * .liquid) -- reuses the same BoxViewer/buildPdfFaceMaterial machinery, but
 * for exactly one box instead of three, with its own construct-when-in-view/
 * dispose-when-out-of-view lifecycle (the same "one screenful's worth of
 * WebGL alive at a time" principle as the hero, just for a single section
 * instead of a whole 3-column row or a whole hero).
 *
 * Also drives the "open box" flow: click Open Box -> the box snaps back to
 * its resting pose and shakes -> a foil bag flies out over it -> drag the
 * bag's top tab to unzip the seam -> the bag falls away and the revealed
 * character flies into the next empty collectible slot. Once all
 * OPEN_BOX_SLOT_COUNT slots are filled, the button switches to Reset.
 */
class PeepzCharacterSectionComponent extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('[data-peepz-box-canvas]');
    this.viewer = null;
    if (!this.canvas) return;

    if ('IntersectionObserver' in window) {
      // Debounced the same way as the hero's whole-section observer --
      // see that comment for why (a transient false reading shouldn't
      // tear down a box that's still genuinely on screen).
      let disposeTimer = null;
      this._observer = new IntersectionObserver(
        (entries) => {
          const isVisible = entries[0].isIntersecting;
          if (isVisible) {
            if (disposeTimer !== null) {
              clearTimeout(disposeTimer);
              disposeTimer = null;
            }
            this._init();
          } else if (disposeTimer === null) {
            disposeTimer = setTimeout(() => {
              disposeTimer = null;
              this._dispose();
            }, 400);
          }
        },
        { threshold: 0, rootMargin: '200px' }
      );
      this._observer.observe(this);
      this._clearDisposeTimer = () => {
        if (disposeTimer !== null) clearTimeout(disposeTimer);
      };
    } else {
      this._init();
    }

    // Bound to a dedicated handle, not the canvas/box itself -- the box
    // stays free for normal page scroll (touch-action:none there would
    // otherwise trap any touch that starts on the box, which is most of
    // the screen on mobile, into rotating it instead of scrolling).
    this._dragHandle = this.querySelector('[data-peepz-drag-handle]');
    if (this._dragHandle) {
      this._dragging = false;
      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._dragHandle.addEventListener('pointerdown', this._onPointerDown);
      window.addEventListener('pointermove', this._onPointerMove);
      window.addEventListener('pointerup', this._onPointerUp);
    }

    this._setUpOpenBox();
  }

  _setUpOpenBox() {
    this.openButton = this.querySelector('[data-peepz-open-box]');
    this.bagLayer = this.querySelector('[data-peepz-unbox-bag]');
    this.bagCanvas = this.querySelector('[data-peepz-unbox-bag-canvas]');
    this.bagSnapshotImg = this.querySelector('[data-peepz-unbox-bag-snapshot]');
    this.bagCanvasWrap = this.querySelector('[data-peepz-unbox-bag-canvas-wrap]');
    this.characterImg = this.querySelector('[data-peepz-unbox-character-img]');
    this.tearHandle = this.querySelector('[data-peepz-unbox-tear-handle]');
    this._boxWrap = this.canvas ? this.canvas.closest('.peepz-character__box-wrap') : null;
    this.collectibles = Array.from(this.querySelectorAll('[data-peepz-collectible]')).sort(
      (a, b) => Number(a.dataset.peepzCollectible) - Number(b.dataset.peepzCollectible)
    );
    if (!this.openButton || !this.bagLayer || !this.tearHandle || !this._boxWrap || !this.collectibles.length) return;

    this._bagSnapshotPromise = null;
    this._unboxing = false;
    this._tearJitter = buildTearJitter(OPEN_BOX_TEAR_STEPS, OPEN_BOX_TEAR_JITTER, Math.random() * 1000);
    this._openedCount = this.collectibles.filter((el) => el.classList.contains('is-filled')).length;
    this._updateOpenButtonLabel();
    // Initializes --tear and the bag canvas's clip-path to the sealed state.
    this._applyTear(0);

    this._onOpenBoxClick = this._onOpenBoxClick.bind(this);
    this._onShakeEnd = this._onShakeEnd.bind(this);
    this._onTearDown = this._onTearDown.bind(this);
    this._onTearMove = this._onTearMove.bind(this);
    this._onTearUp = this._onTearUp.bind(this);

    this.openButton.addEventListener('click', this._onOpenBoxClick);
    this._boxWrap.addEventListener('animationend', this._onShakeEnd);
    this.tearHandle.addEventListener('pointerdown', this._onTearDown);
    this.tearHandle.addEventListener('pointermove', this._onTearMove);
    this.tearHandle.addEventListener('pointerup', this._onTearUp);
    this.tearHandle.addEventListener('pointercancel', this._onTearUp);
  }

  // Renders the bag to a static snapshot (see renderBagSnapshot -- a live
  // second WebGL context for the whole open-box interaction was leaving
  // the box's own context blank partway through) and swaps it in for the
  // placeholder canvas. Cached per section instance since the bag looks
  // the same every pull.
  _ensureBagSnapshot() {
    if (this._bagSnapshotPromise || !this.bagCanvas || !this.bagSnapshotImg) return;
    this._bagSnapshotPromise = renderBagSnapshot(this.bagCanvas.dataset.patternUrl, this.bagCanvas.dataset.shimmerPdfUrl)
      .then((dataUrl) => {
        this.bagSnapshotImg.src = dataUrl;
        this.bagSnapshotImg.hidden = false;
        this.bagCanvas.hidden = true;
      })
      .catch((err) => {
        console.warn('Peepz character section: bag snapshot failed', this.bagCanvas, err);
      });
  }

  _startBoxKeepAlive() {
    if (this._keepAliveId !== null && this._keepAliveId !== undefined) return;
    const tick = () => {
      if (this.viewer) this.viewer.render();
      this._keepAliveId = requestAnimationFrame(tick);
    };
    this._keepAliveId = requestAnimationFrame(tick);
  }

  _stopBoxKeepAlive() {
    if (this._keepAliveId !== null && this._keepAliveId !== undefined) {
      cancelAnimationFrame(this._keepAliveId);
    }
    this._keepAliveId = null;
  }

  _onOpenBoxClick() {
    if (this._unboxing) return;
    if (this._openedCount >= OPEN_BOX_SLOT_COUNT) {
      this._resetCollectibles();
      return;
    }
    this._unboxing = true;
    this.openButton.disabled = true;

    // On mobile, brings the WHOLE section's top edge up to sit just below
    // the sticky header, rather than centering any one element -- the
    // shake/fly-out/land sequence takes over a second and moves around
    // (box, then bag, then tear handle, then the reveal), so anchoring to
    // the section itself keeps the whole thing in frame throughout,
    // instead of centering on the tear handle specifically and leaving
    // the box shake half off-screen above it. --header-end is the same
    // theme-wide custom property (set by main.js) every other sticky-
    // header-aware section already measures itself against.
    if (window.matchMedia('(max-width: 749px)').matches) {
      const headerEnd = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-end')) || 0;
      const targetY = window.scrollY + this.getBoundingClientRect().top - headerEnd;
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }

    // Snap back to the resting view before shaking -- a predictable
    // starting pose regardless of however far the box has been
    // auto-rotated or manually dragged.
    if (this.viewer) {
      this.viewer.stopAutoRotate();
      this.viewer.setManualRotation(0, 0);
    }

    // Keep re-rendering the box for the whole interaction rather than
    // relying on that one setManualRotation() frame to stay correct on
    // the canvas untouched for several seconds. Confirmed this isn't a
    // lost WebGL context (no webglcontextlost fires) -- but whatever it
    // is, continuously redrawing a scene that isn't actually changing is
    // cheap, and directly papers over a single bad/stale frame instead of
    // needing to know why one occurred.
    this._startBoxKeepAlive();

    // Deferred one frame, not called synchronously right here -- starting
    // a brand new WebGL context and its PDF-shimmer pipeline in the exact
    // same tick as the box keep-alive loop kicking off was a plausible
    // trigger for the GPU-process crash/reload reported right at "press
    // Open Box, box gives a little shake" on iOS. But it still needs to
    // fire close to immediately, not later (e.g. after the shake
    // animation, tried first): the bag's fly-out reveals a container the
    // snapshot needs to already be painted into, and delaying the snapshot
    // request meant the (now fully unmasked, see _applyTear's own comment)
    // character image was visible on its own for a beat before the bag
    // graphic caught up and covered it.
    requestAnimationFrame(() => this._ensureBagSnapshot());

    const nextSlot = this.collectibles.find((el) => !el.classList.contains('is-filled'));
    if (nextSlot && this.characterImg) this.characterImg.src = nextSlot.dataset.image || '';

    if (REDUCED_MOTION) {
      this._afterShake();
      return;
    }
    this.classList.add('is-shaking');
  }

  _onShakeEnd(event) {
    if (event.target !== this._boxWrap || !this.classList.contains('is-shaking')) return;
    this.classList.remove('is-shaking');
    this._afterShake();
  }

  _afterShake() {
    this.classList.add('is-bag-out');
    this.bagLayer.addEventListener('animationend', () => this.classList.add('is-bag-landed'), { once: true });
  }

  // Only the top seam of the bag canvas is clipped -- a jagged notch that
  // unzips left-to-right as it's torn, letting the (static, already
  // positioned) character image peek through underneath. The character
  // image itself is NOT also clipped to that jagged notch -- it just sits
  // there, unmasked, and relies on the bag canvas to cover it. (A jagged
  // clip-path on the character too was tried, to plug a margin the bag's
  // WebGL canvas left transparent around its own edges -- but now that
  // resize() fits the camera to the bag's actual rectangle instead of its
  // bounding sphere, that margin is gone, and clipping the character to
  // the same notch shape just gave it an unwanted serrated edge.)
  _applyTear(fraction) {
    this._tear = clamp01(fraction);
    this.style.setProperty('--tear', String(this._tear));
    const pts = bagSeamPoints(this._tear, this._tearJitter, OPEN_BOX_TEAR_STEPS, OPEN_BOX_TOP_SEAM_INSET);
    this.bagCanvasWrap.style.clipPath = `polygon(0% 100%, 100% 100%, 100% 0%, ${pointsToCss([...pts].reverse())})`;
  }

  _onTearDown(event) {
    if (!this.classList.contains('is-bag-landed') || this.classList.contains('is-revealed')) return;
    this._tearDragging = true;
    this._tearMaxReached = 0;
    this.classList.add('is-dragging');
    this.tearHandle.setPointerCapture(event.pointerId);
    this._onTearMove(event);
  }

  // Tear fraction tracks the pointer's ABSOLUTE position across the bag
  // (0 at its left edge, 1 at its right), not displacement from wherever
  // the drag started -- displacement-based tracking meant the result
  // depended on exactly where on the handle you happened to grab it
  // (itself already offset 8% in), which made "how far across" hard to
  // predict and, in practice, landed short of completing far more often
  // than it visually looked like it should.
  _onTearMove(event) {
    if (!this._tearDragging) return;
    const bagRect = this.bagLayer.getBoundingClientRect();
    if (bagRect.width === 0) return;
    const fraction = clamp01((event.clientX - bagRect.left) / bagRect.width);
    // Track the furthest point actually reached during the drag, not just
    // wherever the pointer happens to be at release -- a fast flick/swipe
    // can end with pointerup landing back at a lower reading (browsers
    // don't guarantee a pointermove fires for every pixel of a fast
    // gesture, and a released finger can register a slightly different X
    // than its last moving position), which was silently failing drags
    // that visually looked like they made it.
    this._tearMaxReached = Math.max(this._tearMaxReached, fraction);
    this._applyTear(fraction);
  }

  _onTearUp() {
    if (!this._tearDragging) return;
    this._tearDragging = false;
    this.classList.remove('is-dragging');
    if (this._tearMaxReached > 0.35) {
      this._completeReveal();
    } else {
      // Didn't pull far enough to unzip the whole seam -- settle back
      // rather than snapping all the way shut.
      this._applyTear(Math.min(this._tear, 0.2));
    }
  }

  _completeReveal() {
    this._applyTear(1);
    this.classList.add('is-revealed');
    if (REDUCED_MOTION) {
      this.classList.add('is-fallen');
      this._afterFallAway();
      return;
    }
    // The clip-path transition to a fully-open seam (see the
    // :not(.is-dragging) rule above) takes 0.35s -- this has to wait at
    // least that long, otherwise the fall-away animation yanks the bag
    // away mid-transition and the tear visibly stops short of the full
    // width instead of finishing.
    window.setTimeout(() => {
      this.classList.add('is-fallen');
      this.bagCanvasWrap.addEventListener('animationend', () => this._afterFallAway(), { once: true });
    }, 400);
  }

  _afterFallAway() {
    const slot = this.collectibles.find((el) => !el.classList.contains('is-filled'));
    if (slot) {
      this._flyToSlot(slot, this.characterImg ? this.characterImg.src : '');
      slot.classList.remove('is-locked');
      slot.classList.add('is-filled');
      this._openedCount += 1;
    }
    this._updateOpenButtonLabel();

    this.classList.remove('is-bag-out', 'is-bag-landed', 'is-revealed', 'is-fallen', 'is-dragging');
    this._applyTear(0);
    this._tearJitter = buildTearJitter(OPEN_BOX_TEAR_STEPS, OPEN_BOX_TEAR_JITTER, Math.random() * 1000);
    this._unboxing = false;
    this.openButton.disabled = false;

    // Belt-and-suspenders on top of the bag now being a one-shot snapshot
    // (see renderBagSnapshot) rather than a live second WebGL context for
    // the whole interaction: still put the box through the same dispose/
    // reconstruct cycle scrolling the section away and back already does,
    // in case anything left it in a bad state.
    this._stopBoxKeepAlive();
    this._dispose();
    this._init();
  }

  // A cloned image that visually travels from the (now-empty) bag position
  // to its destination slot, fixed-positioned so it isn't clipped by any
  // ancestor's overflow -- purely decorative, the slot's own <img> (already
  // in the DOM, just hidden via CSS until .is-filled) is the real content.
  _flyToSlot(slot, imageUrl) {
    if (REDUCED_MOTION || !imageUrl) return;
    const slotImg = slot.querySelector('.peepz-character__collectible-image');
    if (!slotImg) return;
    const startRect = this.bagLayer.getBoundingClientRect();
    const endRect = slotImg.getBoundingClientRect();
    if (startRect.width === 0 || endRect.width === 0) return;

    const flyer = document.createElement('img');
    flyer.src = imageUrl;
    flyer.alt = '';
    flyer.className = 'peepz-character__unbox-flyer';
    const flyerWidth = startRect.width * 0.5;
    flyer.style.width = `${flyerWidth}px`;
    flyer.style.left = `${startRect.left + startRect.width / 2}px`;
    flyer.style.top = `${startRect.top + startRect.height / 2}px`;
    document.body.appendChild(flyer);

    const dx = endRect.left + endRect.width / 2 - (startRect.left + startRect.width / 2);
    const dy = endRect.top + endRect.height / 2 - (startRect.top + startRect.height / 2);
    const endScale = endRect.width / flyerWidth;

    requestAnimationFrame(() => {
      flyer.style.transform = `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(${endScale})`;
      flyer.style.opacity = '0';
    });
    flyer.addEventListener('transitionend', () => flyer.remove(), { once: true });
    window.setTimeout(() => flyer.remove(), 900); // safety net if transitionend doesn't fire
  }

  _updateOpenButtonLabel() {
    this.openButton.textContent = this._openedCount >= OPEN_BOX_SLOT_COUNT ? 'Nulstil' : 'Åbn boksen';
  }

  _resetCollectibles() {
    this.collectibles.forEach((el) => {
      el.classList.remove('is-filled');
      el.classList.add('is-locked');
    });
    this._openedCount = 0;
    this._updateOpenButtonLabel();
  }

  // Drag-to-rotate: 1:1 with pointer movement (see BoxViewer.setManualRotation),
  // no easing back afterward -- it stays wherever you leave it, same as
  // physically turning a box over in your hands.
  _onPointerDown(event) {
    if (!this.viewer) return;
    this.viewer.stopAutoRotate();
    this._dragging = true;
    this._dragStartX = event.clientX;
    this._dragStartY = event.clientY;
    this._dragStartRotY = this.viewer.box.rotation.y;
    this._dragStartRotX = this.viewer.box.rotation.x;
    this._dragHandle.setPointerCapture(event.pointerId);
  }

  _onPointerMove(event) {
    if (!this._dragging || !this.viewer) return;
    const DRAG_SPEED_Y = 0.01; // rad per px, horizontal drag -> turn
    const DRAG_SPEED_X = 0.006; // rad per px, vertical drag -> tilt
    const MAX_TILT_X = 0.4; // rad -- capped so it can't be dragged into looking like it's toppling
    const dx = event.clientX - this._dragStartX;
    const dy = event.clientY - this._dragStartY;
    const rotY = this._dragStartRotY + dx * DRAG_SPEED_Y;
    const rotX = Math.max(-MAX_TILT_X, Math.min(MAX_TILT_X, this._dragStartRotX - dy * DRAG_SPEED_X));
    this.viewer.setManualRotation(rotY, rotX);
  }

  _onPointerUp() {
    this._dragging = false;
    if (this.viewer) this.viewer.startAutoRotate();
  }

  _init() {
    if (this.viewer) return;
    try {
      this.viewer = new BoxViewer(this.canvas, { color: this.canvas.dataset.boxColor });
      this.viewer.startAutoRotate();
      this._loadFaceArt(this.viewer);
    } catch (err) {
      console.warn('Peepz character section: box viewer failed to initialize', this.canvas, err);
    }
  }

  async _loadFaceArt(viewer) {
    const canvas = this.canvas;
    const jobs = [
      { side: 'front', faceIndex: 4 }, // BoxGeometry [+x,-x,+y,-y,+z,-z] -- 4 = front
      { side: 'right', faceIndex: 0 }, // 0 = right
      // Extra faces only the product-gallery box has art for (the hero drops
      // these faces from its geometry entirely). Baked textures only, no PDF
      // fallback -- a face with no peepziez-box-<slug>-<side>.webp just stays
      // the flat placeholder color.
      { side: 'left', faceIndex: 1 },
      { side: 'top', faceIndex: 2 },
      { side: 'back', faceIndex: 5 },
    ].filter((job) => canvas.dataset[`${job.side}TexUrl`] || canvas.dataset[`${job.side}PdfUrl`]);
    if (!jobs.length) return;

    const wrap = canvas.closest('.peepz-character__box-wrap');
    if (wrap) wrap.classList.add('is-loading');
    try {
      await Promise.all(
        jobs.map(async (job) => {
          try {
            const material = await loadFaceMaterial(canvas, job.side);
            if (!material) return;
            // This section's viewer may have been disposed (scrolled away)
            // or disposed-and-reconstructed as a new instance while this
            // fetch was in flight -- only apply to the still-current one.
            if (this.viewer !== viewer) {
              disposeMaterial(material);
              return;
            }
            await viewer.setFaceMaterial(job.faceIndex, material);
          } catch (err) {
            console.warn('Peepz character section: face art failed to load', job.side, err);
          }
        })
      );
    } finally {
      if (wrap) wrap.classList.remove('is-loading');
    }
  }

  _dispose() {
    if (!this.viewer) return;
    this.viewer.dispose();
    this.viewer = null;
  }

  disconnectedCallback() {
    if (this._observer) this._observer.disconnect();
    if (this._clearDisposeTimer) this._clearDisposeTimer();
    if (this._dragHandle) {
      this._dragHandle.removeEventListener('pointerdown', this._onPointerDown);
      window.removeEventListener('pointermove', this._onPointerMove);
      window.removeEventListener('pointerup', this._onPointerUp);
    }
    if (this.openButton) {
      this.openButton.removeEventListener('click', this._onOpenBoxClick);
      this._boxWrap.removeEventListener('animationend', this._onShakeEnd);
      this.tearHandle.removeEventListener('pointerdown', this._onTearDown);
      this.tearHandle.removeEventListener('pointermove', this._onTearMove);
      this.tearHandle.removeEventListener('pointerup', this._onTearUp);
      this.tearHandle.removeEventListener('pointercancel', this._onTearUp);
    }
    this._stopBoxKeepAlive();
    this._dispose();
  }
}

customElements.define('peepz-character-section', PeepzCharacterSectionComponent);

// Exposed only so tools/ and test pages can import the real classes; the
// storefront just loads this file as a module script and ignores exports.
export { BoxViewer, loadFaceMaterial, buildPdfFaceMaterial, buildBakedFaceMaterial, sharedGL };
